import type { ReactNode, FC } from 'react';
import { css } from '@compiled/react';
import React, { Fragment, useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { SpotlightTarget } from '@atlaskit/onboarding';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType';
import { LAST_UPDATED_SPOTLIGHT_TARGET } from '@confluence/modernize-changeboarding/entry-points/spotlightTargets';
import { EXTENSION_POINT_PAGE_METADATA_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component';
import { PAGE_METADATA_BANNER } from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation';
import { DRAFT_STATUS_LOZENGE_ID } from '@confluence/web-item/entry-points/webItemIDs';
import { getSingleParamInt } from '@confluence/route-manager-utils/entry-points/getSingleParam';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';

import type { LastEditedProps } from './LastEdited';
import { LastEdited } from './LastEdited';

const unpublishedChangesTextStyles = xcss({
	color: 'color.text.subtlest',
});

const unpublishedChangesLinkStyles = css({
	color: token('color.text.subtlest'),
	textDecoration: 'none',
	'&:hover': {
		color: token('color.text.subtlest'),
		textDecoration: 'underline',
	},
	'&:visited': {
		color: token('color.text.subtlest'),
	},
});

// this component is copied from content-header PageMetadataBanner.jsx to avoid circular dependency
// and refactored to ignore other webitems under PAGE_METADATA_BANNER except for the "Unpublished Changes" label that object header uses
export const UnpublishedChangesOrLastEdited: FC<LastEditedProps> = ({
	contentId,
	contentType,
	contentMode,
	spaceKey,
	version,
	hasEditPermission,
}) => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();
	const versionOverride: number | null = getSingleParamInt(queryParams, 'versionOverride', null); // webitemlocation must use versionOverride, and not just version

	const renderLastEdited = () => {
		return (
			<LastEdited
				contentId={contentId}
				version={version}
				contentType={contentType}
				contentMode={contentMode}
				spaceKey={spaceKey}
				hasEditPermission={hasEditPermission}
			/>
		);
	};

	// if not viewing page or blogpost, automatically return lastedited instead of unpublished changes.
	if (!(contentMode === 'view' && (contentType === 'page' || contentType === 'blogpost'))) {
		return renderLastEdited();
	}

	return (
		<Fragment>
			<ExperienceStart name={EXTENSION_POINT_PAGE_METADATA_BANNER_EXPERIENCE} />
			<WebItemLocation
				tagName="span"
				location={PAGE_METADATA_BANNER}
				contentId={contentId}
				version={versionOverride || undefined}
			>
				{({ webItems }) => {
					let unpublishedChangesItem: ReactNode = null;
					const editUrl = getUrlForContentType({
						contentType,
						contentId,
						spaceKey,
						redirectToEditor: true,
					});
					if (webItems.some((item) => item.id === DRAFT_STATUS_LOZENGE_ID)) {
						unpublishedChangesItem = (
							<SpotlightTarget name={LAST_UPDATED_SPOTLIGHT_TARGET}>
								<Box xcss={unpublishedChangesTextStyles}>
									{hasEditPermission && editUrl ? (
										// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
										<a
											href={editUrl}
											data-testid="unpublished-changes-edit-link"
											css={unpublishedChangesLinkStyles}
										>
											<FormattedMessage {...i18n.unpublishedChanges} />
										</a>
									) : (
										<FormattedMessage {...i18n.unpublishedChanges} />
									)}
								</Box>
							</SpotlightTarget>
						);
					}

					return (
						<Fragment>
							<ExperienceSuccess name={EXTENSION_POINT_PAGE_METADATA_BANNER_EXPERIENCE} />
							{unpublishedChangesItem || renderLastEdited()}
						</Fragment>
					);
				}}
			</WebItemLocation>
		</Fragment>
	);
};

const i18n = defineMessages({
	unpublishedChanges: {
		id: 'content-types-header.object-header-items.unpublished-changes.link.text',
		defaultMessage: 'Unpublished changes',
		description:
			'Text that shows on view page header when there are unpublished changes for the page',
	},
});
